import type { ShowcaseBannerParams } from '@yandex-int/k-common/typings/api/models/main-page';
import React from 'react';

import cn from 'classnames';
import { SimpleCallback } from '../../typings';
import { BackgroundImage } from '../background-image/background-image';
import { Button } from '../button/button';
import { UI_KIT_TEXT_TYPOGRAPHY } from '../constants';
import { H1 } from '../header-text/header-text';
import { useIntl } from '../intl/context';
import Link from '../link/link';
import { Text } from '../text/text';

import { ShowcaseBannerTag } from './__tag/showcase-banner__tag';

import './showcase-banner.scss';

interface Props extends ShowcaseBannerParams {
    onClick?: SimpleCallback;
    tabIndex?: number;
    role?: React.AriaRole;
    'aria-hidden'?: boolean;
    isSmall?: boolean;
}

export const ShowcaseBanner = (props: Props) => {
    const intl = useIntl();

    const {
        title,
        text,
        isSmall,
        tag,
        button_text: buttonText = intl.formatMessage({ id: 'showcase.banner.buttonText' }),
        button_link: link,
        desktop_image: desktopImage,
        mobile_image: mobileImage,
        visual_settings: visualSettings,
        onClick,
        tabIndex,
        role,
        'aria-hidden': ariaHidden,
    } = props;

    const {
        background_color: backgroundColor,
        title_color: titleColor,
        description_color: descriptionColor,
    } = visualSettings || {};

    const bannerStyle: React.CSSProperties = {
        backgroundColor,
    };

    const titleStyle: React.CSSProperties = {
        color: titleColor,
    };

    return (
        <Link
            aria-hidden={ariaHidden}
            className={cn('showcase-banner', isSmall && 'showcase-banner-small')}
            onClick={onClick}
            role={role}
            style={bannerStyle}
            tabindex={tabIndex}
            target="_blank"
            theme={Link.THEME.ABSTRACT}
            to={link}
        >
            <BackgroundImage aria-hidden={true} mix="showcase-banner__img" src={desktopImage} />

            <div className="showcase-banner__mobile-img-container">
                <BackgroundImage aria-hidden={true} mix="showcase-banner__img" src={mobileImage} />
                <ShowcaseBannerTag text={tag} />
            </div>

            <div className="showcase-banner__content">
                <div className="showcase-banner__text-content">
                    {!isSmall && <ShowcaseBannerTag text={tag} />}
                    <H1 mix="showcase-banner__title" role="presentation" style={titleStyle} type={Text.TYPE.MARKDOWN}>
                        {title}
                    </H1>
                    <Text
                        className="showcase-banner__descr"
                        color={descriptionColor}
                        type={Text.TYPE.MARKDOWN}
                        typography={UI_KIT_TEXT_TYPOGRAPHY.captionXSRegular}
                    >
                        {text}
                    </Text>
                </div>
                <Button as="span" className="showcase-banner__button" rounded size={Button.SIZE.M}>
                    {buttonText}
                </Button>
            </div>
        </Link>
    );
};
